/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by jessenelson on 3/24/16.
 */
'use strict';
angular.module('prism-ui.package').service('PreviewQueryService',
    function (
        _,
        $cookies,
        $q,
        DynamicPropertiesService,
        DownloadAssetService,
        NotificationService,
        PackageMediaResourceFactory,
        PackageMediaInfoResourceFactory,
        PackageResourceFactory
    ) {

        var self = this;
        var defaultMediaRole = 'Main Video';

        self.retrievedMediaInfo = {};

        var buildChapterLabels = function (media) {
            angular.forEach(media.chapters, function (chapter) {
                chapter.label = 'Marker ' + (parseInt(chapter.index) + 1) + ' - ' + chapter.startTimeCode;
            });
        };

        var getMediaObjects = function (packageContent, type) {

            // previewObject is an augmented version of
            // the packageContent object
            let previewObject = _.merge({}, packageContent);
            let componentKey;

            switch (type) {
                case 'video':
                    componentKey = packageContent.role.name;
                    previewObject.cpatMemberId = packageContent.acceptTemplateMember.id;
                    previewObject.dropdownLabel = componentKey;
                    previewObject.in = packageContent.startTimecode;
                    previewObject.out = packageContent.endTimecode;
                    previewObject.videoMap = packageContent.acceptTemplateMember.acceptProfile.name;
                    if (packageContent.role.name !== defaultMediaRole) {
                        previewObject.name = componentKey;
                    }
                    break;
                case 'videocomponentmaster':
                    componentKey = packageContent.componentRole.name;
                    previewObject.isComponentMaster = true;
                    previewObject.dropdownLabel = `${defaultMediaRole} + ${componentKey}`;
                    break;
                case 'audio':
                    componentKey = defaultMediaRole;
                    let audioMAPName = packageContent.acceptTemplateMember.acceptProfile.name;
                    previewObject.audioMap = packageContent.acceptTemplateMember.acceptProfile.name;
                    previewObject.dropdownLabel = `Audio / ${audioMAPName}: ${packageContent.languageAndRole}`;
                    previewObject.isAudio = true;
                    break;
                case 'cc':
                case 'subtitle':
                    componentKey = packageContent.componentRole.name;
                    let textType = 'CC';
                    if (type === 'subtitle') {
                        textType = 'Subtitle';
                    }
                    previewObject.dropdownLabel = `${textType} - ${packageContent.languageAndRole}`;
                    break;
                case 'adpod':
                    componentKey = defaultMediaRole;
                    previewObject.adpods = packageContent.adPods;
                    break;
                case 'image':
                    componentKey = defaultMediaRole;
                    previewObject.dropdownLabel = packageContent.language.name;
                    previewObject.map = packageContent.acceptTemplateMember.acceptProfile.name;
                    break;
                default:

            }

            buildChapterLabels(packageContent);

            // if component key remained unset, the content object
            // had an invalid type
            if (componentKey) {
                return {
                    component: componentKey,
                    objects: [previewObject]
                };
            } else {
                return null;
            }
        };

        var parseMedia = function (data) {
            var mediaMap = {'Main Video': {}};
            angular.forEach(data, function (mediaItem) {
                var type = mediaItem.type.toLowerCase();

                var media = getMediaObjects(mediaItem, type);
                if (media) {
                    var componentCollection = mediaMap[media.component];

                    if (!componentCollection) {
                        componentCollection = {};
                        mediaMap[media.component] = componentCollection;
                    }
                    if (!componentCollection[type]) {
                        componentCollection[type] = [];
                    }
                    componentCollection[type] = componentCollection[type].concat(media.objects);
                }
            });
            return mediaMap;
        };

        let getReachAssetType = (packageContent) => {
            let assetType = _.toLower(packageContent.type);
            switch (assetType) {
                case 'videocomponentmaster':
                    assetType = 'video';
                    break;
                case 'cc':
                case 'subtitle':
                    assetType = 'document';
                    break;
                default:
                    break;
            }
            return assetType;
        };

        // Fetch the asset URI, then save it to
        // packageContent.mediaPath and the Reach asset type to
        // packageContent.assetType. Return the promise.
        self.getAssetUri = (abstractContent) => {
            let assetType = getReachAssetType(abstractContent);
            let assetId = abstractContent.assetId;
            let contentUse = DownloadAssetService.ContentUse.Proxy;

            abstractContent.assetType = assetType;

            return DynamicPropertiesService.queryForProperties()
                .then(() => {
                    if (assetType === 'image' && DynamicPropertiesService.getPropValueAsBoolean('package.previewWithImageSource')) {
                        contentUse = DownloadAssetService.ContentUse.Source;
                    }
                    return DownloadAssetService.getAssetUri(assetId, assetType, contentUse);
                })
                .then((assetUriData) => {
                    abstractContent.mediaPath = assetUriData.uri;
                })
                .catch(() => {
                    NotificationService.createFailureNotification(
                        'Unable to retrieve media URI',
                        `${_.startCase(contentUse)} not available for asset (${_.toUpper(assetType)} id: ${assetId})`,
                        true,
                        5000);
                });
        };

        let hasAssetUri = (content) => {
            let assetId = _.get(content, 'assetId');
            let type = _.toLower(_.get(content, 'type'));
            let role = _.toLower(_.get(content, 'role.name'));
            return (assetId !== undefined && (type !== 'video' || role === 'main video'));
        };

        let getAssetUris = (abstractContents) => {
            let uriPromises = _.chain(abstractContents)
                .filter(content => hasAssetUri(content))
                .map(self.getAssetUri)
                .value();
            return $q.all(uriPromises).then(() => abstractContents);
        };

        self.getPackageMedia = function (packageID) {
            let mediaPromise = PackageMediaResourceFactory.get({
                id: packageID
            }).$promise;
            return mediaPromise
                .then(packageContents => getAssetUris(packageContents))
                .then(packageContents => parseMedia(packageContents));
        };

        self.getMediaInfo = (packageContentId, primary) => {
            let deferred = $q.defer();
            let mediaInfo = self.retrievedMediaInfo[packageContentId];
            if (mediaInfo) {
                deferred.resolve(mediaInfo);
            } else {
                PackageMediaInfoResourceFactory.get({ id: packageContentId, primary: primary }).$promise
                    .then((mediaInfo) => {
                        self.retrievedMediaInfo[packageContentId] = mediaInfo;
                        deferred.resolve(mediaInfo);
                    })
                    .catch((error) => {
                        NotificationService.createFailureNotification(
                            'Unable to retrieve mediainfo data',
                            `Mediainfo data not available for ${primary ? 'primary' : 'non-primary'}` +
                            ` asset (id: ${packageContentId})`,
                            true,
                            5000);
                    });
            }
            return deferred.promise;
        };

        var createPartnerList = function (partnerPackages) {
            var partnerListPromises = [];
            angular.forEach(partnerPackages, function (partnerPackage) {
                var partnerData = {
                    partner: partnerPackage.partner.name,
                    videoRoles: partnerPackage.videoRoles,
                };

                if (partnerPackage.preRoll) {
                    partnerData.preRoll = partnerPackage.preRoll;
                    partnerData.preRoll.dropdownLabel = `Preroll - ${partnerPackage.preRoll.prePostRollName}`;
                }

                if (partnerPackage.postRoll) {
                    partnerData.postRoll = partnerPackage.postRoll;
                    partnerData.postRoll.dropdownLabel = `Postroll - ${partnerPackage.postRoll.prePostRollName}`;
                }

                if (partnerPackage.breakPreRoll && partnerPackage.breakPostRoll) {
                    partnerData.breakPreRoll = partnerPackage.breakPreRoll;
                    partnerData.breakPreRoll.dropdownLabel =
                        `Break Preroll - ${partnerPackage.breakPreRoll.prePostRollName}`;
                    partnerData.breakPostRoll = partnerPackage.breakPostRoll;
                    partnerData.breakPostRoll.dropdownLabel =
                        `Break Postroll - ${partnerPackage.breakPostRoll.prePostRollName}`;
                }
                partnerListPromises.push(
                    getAssetUris(partnerPackage.artworkAssets).then((artworkContents) => {
                        partnerData.artworkAssets = artworkContents;
                        return partnerData;
                    })
                );
            });

            return $q.all(partnerListPromises);
        };

        self.getPartnerPackages = function (packageID) {
            return PackageResourceFactory.query({id: packageID, sub: 'partnerPackages'}).$promise.then(function (data) {
                return createPartnerList(data);
            });
        };
    });
